// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import { requireConnection } from "./connection";
import { MYMEMORY_FALLBACK_DISABLED, MYMEMORY_LANGPAIR, type Direction } from "./translateLanguages";
import { pickMyMemoryTranslation } from "./translateOutputCheck";
const PUBLIC_API_URL = "https://d29a52ery9.execute-api.us-east-1.amazonaws.com/prod";
const TRANSLATE_URL = `${PUBLIC_API_URL}/translate`;
// Longest text the translate Lambda accepts - keep in sync with MAX_TEXT_LENGTH
// in infra/lambda/translate/index.mjs, which rejects anything longer with a 400.
// The page uses it to stop the learner before the request is sent.
export const MAX_INPUT_CHARS = 5000;
const MAX_CHUNK_BYTES = 450;
// Only used to split ja->X text on sentence boundaries before falling back to
// MyMemory - X->ja text (any non-Japanese source, see OTHER_LANGUAGES in
// translateLanguages.ts) is split on the equivalent Latin punctuation
// instead. Every Direction is either "ja-<code>" or "<code>-ja" by
// construction (see translateLanguages.ts).
const SENTENCE_SPLIT_REGEX_JA = /(?<=[。!?\n])/;
const SENTENCE_SPLIT_REGEX_EN = /(?<=[.!?\n])/;

function utf8ByteLength(text) {
    return new TextEncoder().encode(text).length;
}

function splitIntoChunks(text, splitRegex, maxBytes = MAX_CHUNK_BYTES) {
    const sentences = text.split(splitRegex).filter(Boolean);
    const chunks = [];
    let current = "";

    for (const sentence of sentences) {
        if (current && utf8ByteLength(current + sentence) > maxBytes) {
            chunks.push(current);
            current = "";
        }
        current += sentence;

        // A single sentence can still be longer than the limit on its own.
        while (utf8ByteLength(current) > maxBytes) {
            let sliceEnd = current.length;
            while (sliceEnd > 0 && utf8ByteLength(current.slice(0, sliceEnd)) > maxBytes) sliceEnd -= 1;
            chunks.push(current.slice(0, sliceEnd));
            current = current.slice(sliceEnd);
        }
    }
    if (current) chunks.push(current);
    return chunks;
}

// Our translate endpoint answered with an HTTP error (the Lambda's 4xx/5xx).
// A network-level failure never gets this far: fetch itself rejects with a
// TypeError - which is also how a WAF block or a CORS-less 429 shows up in the
// browser ("TypeError: Failed to fetch") - so the two are told apart by class.
export class GoogleRequestError extends Error {
    readonly status: number;

    constructor(status: number) {
        super(`Google translation request failed (${status})`);
        this.name = "GoogleRequestError";
        this.status = status;
    }
}

// How Google's attempt failed: the request never completed ("network"), the
// endpoint answered with an HTTP error ("http", with `status`), or it answered
// but not with a usable translation ("unexpected").
export interface GoogleFailure {
    kind: "network" | "http" | "unexpected";
    status?: number;
}

function describeGoogleFailure(err: unknown): GoogleFailure {
    if (err instanceof GoogleRequestError) return { kind: "http", status: err.status };
    if (err instanceof TypeError) return { kind: "network" };
    return { kind: "unexpected" };
}

// "fallback_failed": MyMemory errored, ran out of quota or returned no text.
// "fallback_rejected": MyMemory returned text, but none of it was plausible
// (translateOutputCheck.ts). "fallback_disabled": the direction is listed in
// MYMEMORY_FALLBACK_DISABLED, so MyMemory was not asked.
export type TranslationUnavailableReason = "fallback_failed" | "fallback_rejected" | "fallback_disabled";

// Google was unavailable and the MyMemory fallback could not give a trustworthy
// translation either. The page shows "couldn't get a reliable translation"
// (never a rejected string) and offers a retry. `googleFailure` says how Google
// failed first; nothing acts on it yet, it is for diagnosing and (later) telemetry.
export class TranslationUnavailableError extends Error {
    readonly reason: TranslationUnavailableReason;
    readonly googleFailure: GoogleFailure;
    readonly cause?: unknown;

    constructor(reason: TranslationUnavailableReason, googleFailure: GoogleFailure = { kind: "unexpected" }, cause?: unknown) {
        const googleDetail = googleFailure.kind === "http" ? `HTTP ${googleFailure.status}` : googleFailure.kind;
        const causeDetail = cause instanceof Error ? `: ${cause.message}` : "";
        super(`Translation unavailable (${reason}; Google: ${googleDetail})${causeDetail}`);
        this.name = "TranslationUnavailableError";
        this.reason = reason;
        this.googleFailure = googleFailure;
        this.cause = cause;
    }
}

// MyMemory answered, but nothing it offered passed the sanity check.
class MyMemoryRejectedError extends Error {
    constructor() {
        super("Translation fallback returned no plausible translation");
        this.name = "MyMemoryRejectedError";
    }
}

async function translateChunkViaMyMemory(chunk: string, direction: Direction): Promise<string> {
    const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(chunk)}&langpair=${MYMEMORY_LANGPAIR[direction]}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Translation request failed (${response.status})`);
    const data = await response.json();

    // MyMemory returns HTTP 200 even when its free daily quota is exhausted,
    // putting a warning string in the body instead of a real translation -
    // responseStatus carries the real status, and as a fallback the warning
    // text itself has a known, documented prefix.
    if (data.responseStatus && Number(data.responseStatus) !== 200) {
        throw new Error(`Translation request failed (${data.responseStatus})`);
    }
    const translatedText: string = data.responseData?.translatedText ?? "";
    if (/^MYMEMORY WARNING/i.test(translatedText.trim())) {
        throw new Error("Translation quota exceeded");
    }

    // Judged per chunk, against the chunk that was sent: the headline answer, or
    // else the first stored match that passes (see pickMyMemoryTranslation).
    const pick = pickMyMemoryTranslation(data, direction, chunk);
    if (pick.status === "ok") return pick.text;
    if (pick.status === "empty") throw new Error("Translation fallback returned no text");
    throw new MyMemoryRejectedError();
}

// Uses MyMemory's free, keyless translation API (https://mymemory.translated.net) —
// no signup needed, but it caps requests at roughly 500 bytes each, hence the chunking.
// Every chunk has to produce a plausible translation, otherwise the whole thing
// throws: half a translation is worse than none.
async function translateViaMyMemory(text: string, direction: Direction): Promise<string> {
    const splitRegex = direction.endsWith("-ja") ? SENTENCE_SPLIT_REGEX_EN : SENTENCE_SPLIT_REGEX_JA;
    // A whitespace-only chunk (the splitter can leave a lone newline) has nothing
    // to translate and would otherwise count as an empty, failed translation.
    const chunks = splitIntoChunks(text, splitRegex).filter((chunk) => chunk.trim() !== "");
    const translations = await Promise.all(chunks.map((chunk) => translateChunkViaMyMemory(chunk, direction)));
    return translations.join(" ").replace(/\s+/g, " ").trim();
}

// Proxied through our own API so the Google Cloud Translation API key never
// reaches the browser (see infra/lambda/translate).
async function translateViaGoogle(text: string, direction: Direction): Promise<string> {
    const response = await fetch(TRANSLATE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, direction }),
    });
    if (!response.ok) throw new GoogleRequestError(response.status);
    const data = await response.json();
    if (typeof data?.translatedText !== "string") throw new Error("Malformed translation response");
    return data.translatedText;
}

// Tries Google Cloud Translation first for higher-quality results, and falls
// back to MyMemory's free keyless API whenever Google is unavailable (key not
// configured, quota exhausted, network error, etc.) so the feature keeps working -
// but only with a MyMemory answer that passes the sanity check in
// translateOutputCheck.ts; otherwise TranslationUnavailableError is thrown.
// direction is "ja-en" (default) or any other Direction from
// translateLanguages.ts (e.g. "en-ja", "ja-pt", "pt-ja", ...). Japanese is
// always one side; the other side is any OTHER_LANGUAGES entry (currently
// English, Portuguese, Spanish, French, German, Dutch, Italian, Chinese,
// Korean, Russian or Arabic).
export async function translateText(text: string, direction: Direction = "ja-en"): Promise<string> {
    return (await translateTextWithProvider(text, direction)).text;
}

// Which service produced a translation, so the page can credit the right one:
// Google's attribution rules require "powered by Google Translate" next to
// Google's results, and that would be false for a MyMemory fallback. `provider`
// is null only for empty input (nothing was translated).
export type TranslationProvider = "google" | "mymemory";
export interface TranslationResult {
    text: string;
    provider: TranslationProvider | null;
}

// Throws TranslationUnavailableError when Google is unavailable and the fallback
// cannot be trusted either (it failed, answered with nothing plausible, or is
// switched off for this direction): callers get a translation they can show or
// that error, never a MyMemory string that failed translateOutputCheck.ts. Only
// MyMemory's output is checked; Google's is returned as it comes.
export async function translateTextWithProvider(text: string, direction: Direction = "ja-en"): Promise<TranslationResult> {
    requireConnection();
    const trimmed = text.trim();
    if (!trimmed) return { text: "", provider: null };

    let googleFailure: GoogleFailure;
    try {
        return { text: await translateViaGoogle(trimmed, direction), provider: "google" };
    } catch (err) {
        googleFailure = describeGoogleFailure(err);
        console.warn("Google translation unavailable:", err);
    }

    if (MYMEMORY_FALLBACK_DISABLED.includes(direction)) {
        throw new TranslationUnavailableError("fallback_disabled", googleFailure);
    }
    try {
        return { text: await translateViaMyMemory(trimmed, direction), provider: "mymemory" };
    } catch (err) {
        const reason = err instanceof MyMemoryRejectedError ? "fallback_rejected" : "fallback_failed";
        throw new TranslationUnavailableError(reason, googleFailure, err);
    }
}

// Kept for backward compatibility with anything still importing the old
// Japanese-only name.
export async function translateJapaneseToEnglish(text) {
    return translateText(text, "ja-en");
}
